import { CalendarIcon, CalendarPlus, CheckCircle2, ClockIcon, Download } from 'lucide-react'
import PropTypes from 'prop-types'

import ActionButtons from '@/components/ActionButtons/ActionButtons'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/elements/accordion'
import { Button } from '@/components/elements/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/elements/card'
import StatusBadge from '@/components/StatusBadge/StatusBadge'
import { formatDate, formatTimeAndDate, formatTimeRange } from '@/utils/formatters'
import { cn } from '@/utils/helpers'

/**
 * CertificateCard
 * @param {Object} props
 * @param {Object} props.studentCertificate
 * @param {Function} [props.renderOnline] - Per-courseRecord status / control JSX (right column of the modules list).
 * @param {Function} [props.renderHandsOn] - Inline status / control shown in the Hands-On section header.
 * @param {(event: Object) => void} [props.onViewEvent] - Click handler for the "Event Details" button. When omitted the button is hidden.
 * @param {() => void} [props.onScheduleTraining] - Click handler for the "Schedule Training" CTA. When omitted the CTA is hidden.
 * @param {import('@/components/ActionButtons/ActionButtons').ActionButton[]} [props.actions] - Footer action buttons.
 * @param {import('react').JSX.Element} [props.footer] - Raw footer override (takes precedence over `actions`).
 * @param {import('react').JSX.Element} [props.bottomBanner] - Full-bleed element pinned flush to the card's bottom edge (e.g. a destructive "remove" strip).
 * @param {string} [props.className]
 */
function CertificateCard({
  studentCertificate,
  renderOnline,
  renderHandsOn,
  onViewEvent,
  onScheduleTraining,
  actions,
  footer,
  bottomBanner,
  className,
}) {
  const { certificate, event, trainingStatus, trainingCompletedAt, status } = studentCertificate
  const hasTrainingRequirement = trainingStatus !== null
  const isIssued = status === 'Issued'

  const courseRecords = studentCertificate.courseRecords || []
  const totalCourses = courseRecords.length
  const completedCourses = courseRecords.filter((r) => r.status === 'Completed').length
  const progressPct = totalCourses === 0 ? 0 : Math.round((completedCourses / totalCourses) * 100)
  const isCourseworkDone = totalCourses > 0 && completedCourses === totalCourses

  const handsOnCompleted = trainingStatus === 'Completed'
  const trainingScheduled = !!event && !handsOnCompleted

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader>
        <CardTitle>{certificate.name}</CardTitle>
        {certificate.description && <CardDescription>{certificate.description}</CardDescription>}
        <CardAction>
          <StatusBadge status={status} type="studentCertificate" />
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {isIssued && (
          <div className="bg-muted/40 flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-success h-4 w-4 shrink-0" />
              <div className="flex flex-col">
                <span>
                  Issued{' '}
                  <strong className="font-semibold">
                    {formatTimeAndDate(studentCertificate.dateIssued)}
                  </strong>
                </span>
                {studentCertificate.certificateNumber && (
                  <span className="text-muted-foreground font-mono text-xs">
                    {studentCertificate.certificateNumber}
                  </span>
                )}
              </div>
            </div>
            {studentCertificate.document?.signedFilePath && (
              <a
                href={studentCertificate.document.signedFilePath}
                download
                className="text-primary hover:text-foreground inline-flex items-center gap-1 text-xs font-semibold tracking-wide uppercase"
              >
                <Download className="h-4 w-4" />
                Download
              </a>
            )}
          </div>
        )}

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem
            value="modules"
            className="overflow-hidden rounded-lg border last:border-b"
          >
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex flex-1 flex-col gap-2 pr-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold tracking-wide uppercase">
                    Course Progress
                  </span>
                  <span className="text-muted-foreground text-xs font-medium tabular-nums">
                    {completedCourses} / {totalCourses} complete
                  </span>
                </div>
                <div
                  className="bg-muted h-2 w-full overflow-hidden rounded-full"
                  role="progressbar"
                  aria-label="Course progress"
                  aria-valuenow={progressPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500 ease-out',
                      isCourseworkDone ? 'bg-success' : 'bg-primary',
                    )}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pt-0 pb-3">
              <ul className="divide-y">
                {courseRecords.map((courseRecord) => (
                  <li
                    key={courseRecord.id}
                    className="flex items-center justify-between gap-3 py-2 text-sm"
                  >
                    <span className="font-medium">{courseRecord.course.name}</span>
                    {renderOnline && renderOnline(courseRecord)}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {hasTrainingRequirement && (
          <div className="overflow-hidden rounded-lg border">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm font-semibold tracking-wide uppercase">
                Hands-On Training
              </span>
              {renderHandsOn && renderHandsOn(studentCertificate)}
            </div>

            {handsOnCompleted && (
              <div className="bg-muted/40 flex items-center gap-2 border-t px-4 py-3 text-sm">
                <CheckCircle2 className="text-success h-4 w-4 shrink-0" />
                <span>
                  Completed{' '}
                  <strong className="font-semibold">
                    {formatTimeAndDate(trainingCompletedAt)}
                  </strong>
                </span>
              </div>
            )}

            {trainingScheduled && (
              <div className="bg-muted/40 flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <div className="flex flex-col gap-0.5">
                    <p className="font-semibold">{event.name}</p>
                    <p className="text-muted-foreground">
                      {formatDate(event.date)}
                      {event.startTime && event.endTime && (
                        <>
                          {' · '}
                          {formatTimeRange(event.startTime, event.endTime)}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                {onViewEvent && (
                  <Button
                    onClick={() => onViewEvent(event)}
                    size="sm"
                    variant="outline"
                    className="self-start sm:self-auto"
                  >
                    Event Details
                  </Button>
                )}
              </div>
            )}

            {!handsOnCompleted && !event && (
              <div className="text-muted-foreground flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <ClockIcon className="h-4 w-4 shrink-0" />
                  <span>No training session scheduled yet.</span>
                </div>
                {onScheduleTraining && (
                  <Button
                    onClick={onScheduleTraining}
                    size="sm"
                    className="self-start sm:self-auto"
                  >
                    <CalendarPlus className="mr-1 h-4 w-4" />
                    Schedule Training
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {footer ? (
        <CardFooter>{footer}</CardFooter>
      ) : (
        actions &&
        actions.length > 0 && (
          <CardFooter>
            <ActionButtons actions={actions} />
          </CardFooter>
        )
      )}

      {bottomBanner && (
        <div className="mt-auto -mb-6 overflow-hidden rounded-b-xl">{bottomBanner}</div>
      )}
    </Card>
  )
}

export default CertificateCard

CertificateCard.propTypes = {
  studentCertificate: PropTypes.object.isRequired,
  renderOnline: PropTypes.func,
  renderHandsOn: PropTypes.func,
  onViewEvent: PropTypes.func,
  onScheduleTraining: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.object),
  footer: PropTypes.element,
  bottomBanner: PropTypes.element,
  className: PropTypes.string,
}
